import {Box, Typography} from '@mui/material';
import {useState, useRef, useCallback, useEffect, useLayoutEffect} from 'react';
import ArrTooltip from '../../../common/arr-tooltip/ArrTooltip';

type TruncatedTooltipProps = {
  content: any;
  className?: string;
  typotestId?: string;
  labelId?: string;
  dataTestId?: string;
};

const TruncatedTooltip = ({
  content,
  className,
  typotestId,
  dataTestId,
  labelId,
}: TruncatedTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const displayContent = String(content ?? '');

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const truncated = el.clientWidth < el.scrollWidth;

    setIsTruncated(truncated);
  }, []);

  useLayoutEffect(() => {
    checkTruncation();
  }, [displayContent, checkTruncation]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      checkTruncation();
    });

    observer.observe(el);

    if (el.parentElement) {
      observer.observe(el.parentElement);
    }

    return () => observer.disconnect();
  }, [checkTruncation]);

  const text = (
    <Typography
      component="div"
      ref={ref}
      className={className}
      data-testid={typotestId}
      noWrap
    >
      {displayContent}
    </Typography>
  );

  return (
    <Box data-testid={dataTestId} tabIndex={-1}>
      {isTruncated ? (
        <ArrTooltip
          content={displayContent}
          hideInfoIconOnEmptyChildren
          labelId={labelId}
        >
          {text}
        </ArrTooltip>
      ) : (
        text
      )}
    </Box>
  );
};

export default TruncatedTooltip;
